import {
  createRef,
  Fragment,
  memo,
  useCallback,
  useRef,
  useEffect,
  useMemo
} from "react";
import { useFormikContext } from "formik";
import { useItemContext } from "../../hooks/useItem";
import { useDataRefObj } from "../../hooks/useDataRef";
import { DragAndDrop } from "../../hooks/useDragAndDrop";
import { useGetMemItem } from "../../hooks/useMemoryDB";
import { Row, Col, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faCalendar } from "@fortawesome/free-solid-svg-icons";
import { formatCondition } from "../conditionsBox";
import { withSchedule } from "../scheduleDiagram/schedule";
import { formatStatement } from "./rule";

import idGen from "../../utils/idGen";

import Container from "../container";
import Metadata from "../metadata";
import ContentType from "../contentType";
import Rule from "./rule";
import Clauses from "./clauses";
import LabelSelect from "../selects/selectLabel";
import AndOrSelector from "../query/andOrSelector";

const labelStyle = { width: "400px" };
const labelTypes = ["rules"];
// The rule info is only read (validation, conditions, metadata): keep the
// same object while its content doesn't change.
const infoOptions = { keepEqual: true };

// Memoized: the header of every statement (condition parsing included) was
// rebuilt at every keystroke. A statement is replaced, not mutated, when it
// changes.
const StatementHeader = memo(function StatementHeader({ rule }) {
  return (
    <div className="rule-header">
      <span className="if">if</span>
      {formatCondition(rule.condition)}
      <span className="then">then</span>
      {formatStatement(rule)}
    </div>
  );
});

const BusinessRule = (props) => {
  const ref = props.ref;
  const refsMap = useRef(new Map());
  const errorsMap = useRef(new Set());
  const { values, errors, setFieldValue, setValues, handleChange } =
    useFormikContext();
  const info = useDataRefObj("rules", values, infoOptions);
  const { isCopy } = useItemContext();
  const labelMain = useGetMemItem("labelsAll", values.label);

  ref.current.info = info;

  // The scheduling of the rule (scheduled, scheduling, schedulingMap) is a
  // summary of the statements' own scheduled/scheduling (withSchedule): it is
  // computed again at every write of the statements (the edit of a condition,
  // a delete, an add, a move) and written only when it changes, so that a
  // rule without scheduling gets no new fields. A map by statement index kept
  // apart from the statements, and updated only by the edits of a condition,
  // kept the ranges of a deleted statement, and the old indexes after a
  // delete or a move. One setValues: the statements and the summary together.
  const writeStatements = useCallback(
    (rule) => setValues((values) => withSchedule({ ...values, rule })),
    [setValues]
  );

  const emptyRule = useCallback(
    () => ({
      _id: idGen(),
      condition: `*`,
      boost: 1,
      type: "I2I"
    }),
    []
  );

  const addEmptyRule = useCallback(() => {
    writeStatements([...(values.rule || []), emptyRule()]);
  }, [emptyRule, values.rule, writeStatements]);

  useEffect(() => {
    if (values.contentType && !values.rule) {
      addEmptyRule();
    }
  }, [addEmptyRule, values.contentType, values.rule]);

  // The edit of a condition (see Rule): the values with the condition and
  // the scheduled/scheduling of that statement.
  const onChangeConditionValues = useCallback(
    (values) => setValues(withSchedule(values)),
    [setValues]
  );

  // A drag and drop of a statement (useDragAndDrop): the moved statements.
  const onMoveStatements = useCallback(
    (_, rule) => writeStatements(rule),
    [writeStatements]
  );

  useEffect(() => {
    if (isCopy) {
      const rules = values?.rule || [];
      rules.forEach((rule) => {
        rule._id = idGen();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeContentType = useCallback(
    (contentType, realm) => {
      setValues({ ...values, contentType, realm, field: undefined });
    },
    [setValues, values]
  );

  const doDelete = useCallback(
    (ruleIndex) => {
      const newValue = [...values.rule];
      newValue.splice(ruleIndex, 1);
      writeStatements(newValue);
    },
    [values.rule, writeStatements]
  );

  const footer = useCallback(
    (index) => {
      if (values.rule?.length <= 1) return null;
      return (
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => doDelete(index)}
          style={{ float: "right" }}
        >
          <FontAwesomeIcon icon={faTrashAlt} className="delete-icon" /> Delete
          Statement
        </Button>
      );
    },
    [doDelete, values.rule]
  );

  const mainFooter = useMemo(() => {
    return (
      <Button size="sm" variant="outline-primary" onClick={addEmptyRule}>
        Add Statement
      </Button>
    );
  }, [addEmptyRule]);

  const title = useMemo(() => {
    return (
      <Fragment>
        Statements{" "}
        <span className="statements-count-badge">
          {values?.rule?.length || 0}
        </span>
      </Fragment>
    );
  }, [values.rule]);

  const extraLabel = useMemo(() => {
    if (labelMain) {
      const { background, color, value } = labelMain;
      return (
        <div
          className="label-main"
          style={{ backgroundColor: background, color: color }}
        >
          {value}
        </div>
      );
    }
  }, [labelMain]);

  const mainHeader = useMemo(() => {
    return (
      <div className="main-header">
        <span className="label">Catalog</span>
        <span className="clause">{values.contentType}</span>
        <span className="label">Clauses</span>
        {values.clauses?.map((c) => {
          return (
            <span className={`clause clause-${c}`} key={c}>
              {c === "filter" ? "Keep Only" : c}
            </span>
          );
        })}
      </div>
    );
  }, [values]);

  const statementsExtra = useMemo(() => {
    if (!values.rule?.length > 1) return null;

    return (
      <div className="statements-extra">
        {values.grouped && (
          <div className="and-or">
            <AndOrSelector
              operator={values.operator}
              defaultOperator="or"
              onChangeOperator={(e) => setFieldValue("operator", e)}
              showTie={false}
            />
          </div>
        )}
        <Form.Check
          type="switch"
          id="group-statements"
          checked={values.grouped ?? false}
          value="true"
          onChange={(e) => setFieldValue("grouped", e.target.checked)}
          name="group-statements"
          label="Group"
        />
      </div>
    );
  }, [setFieldValue, values.grouped, values.operator, values.rule?.length]);

  const settingsStartFolded = useRef(values.contentType !== undefined);
  const handleLabelChange = useCallback(
    (v) => setFieldValue("label", v),
    [setFieldValue]
  );

  return (
    <span>
      <Container
        title="Settings"
        help="rule-main-params"
        color="#06141a"
        folded={settingsStartFolded.current}
        header={mainHeader}
        extra={extraLabel}
        warning={errors.settings}
      >
        <Row className="settings-row">
          <Col sm="1">Label</Col>
          <Col sm="3">
            <LabelSelect
              types={labelTypes}
              style={labelStyle}
              value={values.label}
              handleChange={handleLabelChange}
            />
          </Col>
        </Row>
        <Row className="settings-row">
          <Col sm="1">Catalog</Col>
          <Col sm="3">
            <ContentType
              rootNode={values}
              fixedValue={values.contentType}
              onChange={handleChangeContentType}
              onlySelect={true}
            />
          </Col>
        </Row>

        <Clauses />
      </Container>

      {values.contentType ? (
        <span>
          <span>
            <Metadata
              rootNode={values.metadata}
              rootNodeName="metadata"
              rootNodePath="metadata."
              contentType={values.contentType}
              uniqueId={`rule-${values.id}`}
              info={info}
            />

            <Container
              title={title}
              help="rule-statements"
              color="#0b111a"
              footer={mainFooter}
              warning={errors.statements}
              extra={statementsExtra}
            >
              <DragAndDrop>
                {values.rule &&
                  values.rule.map((r, i) => {
                    if (!refsMap.current.has(r._id)) {
                      refsMap.current.set(r._id, createRef());
                    }
                    const error = errors.statementsList?.[r._id]?.error;
                    if (error && !errorsMap.current.has(r._id))
                      errorsMap.current.add(r._id);

                    const title = (
                      <>
                        {r.scheduled ? (
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="statement-scheduled-icon"
                          />
                        ) : null}
                        {`#id: ${r._id}`}
                      </>
                    );

                    return (
                      <Container
                        id={r._id}
                        key={r._id}
                        ref={refsMap.current.get(r._id)}
                        title={title}
                        help="rule-statement"
                        color="#180c0c"
                        coloredBars={false}
                        coloredTitle={true}
                        hideBodyOnFold={true}
                        footer={footer(i)}
                        warning={error}
                        folded={!errorsMap.current.has(r._id)} //!error
                        header={<StatementHeader rule={r} />}
                        extra={
                          i !== values.rule.length - 1 ? (
                            <Form.Check
                              type="switch"
                              checked={r.break === true ? true : false}
                              value={true}
                              onChange={handleChange}
                              name={`rule[${i}].break`}
                              id={`rule[${i}].break`}
                              label="Skip Others If Executed"
                              className="skip-switch"
                            />
                          ) : null
                        }
                      >
                        <Rule
                          ref={refsMap.current.get(r._id)}
                          rule={r}
                          ruleIndex={i}
                          rootNode={values["rule"][i]}
                          rootNodePath={`rule[${i}].`}
                          onChangeConditionValues={onChangeConditionValues}
                          onMove={onMoveStatements}
                          info={info}
                        />
                      </Container>
                    );
                  })}
              </DragAndDrop>
            </Container>
          </span>
        </span>
      ) : null}
    </span>
  );
};

export default BusinessRule;
